import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { COMPANY, IMAGES, LOGO_TAGLINE } from '../constants/config';

const HOLD_MS = 3200;
const FADE_MS = 0.45;

/**
 * Logo lockup: [icon] [ TOWEREDGE ]
 *                      [ tagline  ]  ← centered under wordmark
 * Tagline cycles the two statements one at a time.
 */
function BrandLogo({
  className = '',
  size = 'nav', // 'nav' | 'footer'
  animateTagline = true,
  loading = 'eager',
  taglineAlign = 'center', // 'center' | 'left'
}) {
  const reduceMotion = useReducedMotion();
  const lines = LOGO_TAGLINE.loop;
  const [index, setIndex] = useState(0);
  const wordmarkRef = useRef(null);
  const sizerRef = useRef(null);
  const [wordmarkWidth, setWordmarkWidth] = useState(0);
  const [scaleX, setScaleX] = useState(1);

  useEffect(() => {
    if (!animateTagline || reduceMotion || lines.length < 2) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIndex((current) => (current + 1) % lines.length);
    }, HOLD_MS);

    return () => window.clearTimeout(timeoutId);
  }, [animateTagline, reduceMotion, lines.length, index]);

  const line = lines[index] || lines[0];
  const taglineLeft = taglineAlign === 'left';
  const taglineX = taglineLeft ? 0 : '-50%';

  // Keep the tagline's rendered width pinned to the wordmark's rendered width,
  // since the two tagline strings differ in length but must line up under the logo.
  useEffect(() => {
    const img = wordmarkRef.current;
    if (!img) {
      return undefined;
    }

    const updateWidth = () => setWordmarkWidth(img.getBoundingClientRect().width);

    if (img.complete) {
      updateWidth();
    }
    img.addEventListener('load', updateWidth);

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(img);

    return () => {
      img.removeEventListener('load', updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!sizerRef.current || !wordmarkWidth) {
      return;
    }

    const naturalWidth = sizerRef.current.getBoundingClientRect().width;
    if (naturalWidth > 0) {
      setScaleX((wordmarkWidth * 0.9) / naturalWidth);
    }
  }, [line, wordmarkWidth]);

  const sizeClass = size === 'nav' ? 'brand-logo-nav-size' : `brand-logo--${size}`;

  return (
    <span className={`brand-logo ${sizeClass} inline-flex items-center ${className}`}>
      <img
        src={IMAGES.logoIcon}
        alt=""
        aria-hidden="true"
        loading={loading}
        decoding="async"
        className="brand-logo-icon block flex-shrink-0 object-contain"
      />
      <span
        className={`brand-logo-text flex min-w-0 flex-col ${taglineLeft ? 'items-start' : 'items-center'}`}
        style={wordmarkWidth ? { width: `${wordmarkWidth}px` } : undefined}
      >
        <img
          ref={wordmarkRef}
          src={IMAGES.logoWordmark}
          alt={COMPANY.name}
          loading={loading}
          decoding="async"
          className="brand-logo-wordmark block w-auto object-contain"
        />
        <span
          className={`brand-tagline relative flex w-full ${taglineLeft ? 'justify-start text-left' : 'justify-center text-center'}`}
          aria-live="polite"
        >
          <span
            ref={sizerRef}
            className="invisible absolute block select-none whitespace-nowrap"
            aria-hidden="true"
          >
            {line}
          </span>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={line}
              className={`absolute top-0 whitespace-nowrap ${taglineLeft ? 'left-0' : 'left-1/2'}`}
              style={{ transformOrigin: taglineLeft ? 'left center' : 'center center' }}
              initial={reduceMotion ? { x: taglineX, scaleX } : { opacity: 0, y: 3, x: taglineX, scaleX }}
              animate={{ opacity: 1, y: 0, x: taglineX, scaleX }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -3, x: taglineX, scaleX }}
              transition={{ duration: FADE_MS, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.span>
          </AnimatePresence>
        </span>
      </span>
    </span>
  );
}

export default BrandLogo;
